import ApiService from "../../../../services/ApiService";
import translate from "../../../../services/Translate";
import { ModuleStatusKey, StatusResult, programModule } from "./CohortProgramManager.d";

const EXAM_STATUSES = {
  FAILED: ["failed", "repass"],
  SUCCESS: ["success"],
  WAITING_CORRECTION: ["correction_pending", "pre_corrected", "being_corrected", "pre_correction_in_progress"],
  NO_ATTEMPT: ["created"],
  IN_PROGRESS: ["in_progress"],
};

const _getModuleStatusSortValue = (status: ModuleStatusKey): number => {
  switch (status) {
    case "validated":
      return 4;
    case "waiting_correction":
      return 3;
    case "failed":
      return 2;
    case "in_progress":
    case "no_attempt":
      return 1;
    case "not_started":
    case "no_exam":
    default:
      return 0;
  }
};

const getExamStatusLabel = (exam: any): string => (exam.latest_attempt_status || "").toLowerCase();

const getTotalAttemptsCount = (module: programModule): number =>
  (module.exams || []).reduce((total, exam) => total + (exam.attempts_count || 0), 0);

const getFailedAttemptsCountFromExam = (exam: any): number => {
  const status = getExamStatusLabel(exam);
  const attemptsCount = exam.attempts_count || 0;

  if (EXAM_STATUSES.SUCCESS.includes(status as any)) return 0;
  if (EXAM_STATUSES.FAILED.includes(status as any)) return attemptsCount;
  return Math.max(attemptsCount - 1, 0);
};

const _getTotalFailedAttemptsCount = (module: programModule): number =>
  (module.exams || []).reduce((total, exam) => total + getFailedAttemptsCountFromExam(exam), 0);

const formatAttemptsLabel = (count: number, suffix: string): string => {
  if (count <= 0) return "";
  return ` (${count} ${count > 1 ? `${suffix}s` : suffix})`;
};

const moduleHasExamWithStatus = (module: programModule, statuses: readonly string[]): boolean =>
  (module.exams || []).some((exam) => statuses.includes(getExamStatusLabel(exam)));

export const resolveModuleStatus = (module: programModule | undefined, language: string): StatusResult => {
  if (!module) {
    return {
      label: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.NOT_STARTED"),
      variant: "neutral",
      sortValue: _getModuleStatusSortValue("not_started"),
    };
  }

  const exams = module.exams || [];

  if (
    module.is_validated ||
    (exams.length &&
      exams.every((exam) => exam.is_validated || EXAM_STATUSES.SUCCESS.includes(getExamStatusLabel(exam))))
  ) {
    return {
      label: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.VALIDATED"),
      icon: "dst-icon-check",
      variant: "success",
      sortValue: _getModuleStatusSortValue("validated"),
    };
  }

  if (module.has_started_module === false) {
    return {
      label: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.NOT_STARTED"),
      icon: "dst-icon-book-closed",
      variant: "neutral",
      sortValue: _getModuleStatusSortValue("not_started"),
    };
  }

  if (exams.length === 0) {
    return {
      label: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.NO_EXAM"),
      icon: "dst-icon-file-x-03",
      variant: "neutral",
      sortValue: _getModuleStatusSortValue("no_exam"),
    };
  }

  if (moduleHasExamWithStatus(module, EXAM_STATUSES.FAILED) || _getTotalFailedAttemptsCount(module) > 0) {
    return {
      label: `${translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.FAILED")}${formatAttemptsLabel(
        _getTotalFailedAttemptsCount(module),
        translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.ATTEMPT.FAILED")
      )}`,
      icon: "dst-icon-x",
      variant: "danger",
      sortValue: _getModuleStatusSortValue("failed"),
    };
  }

  if (moduleHasExamWithStatus(module, EXAM_STATUSES.WAITING_CORRECTION)) {
    return {
      label: `${translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.WAITING_CORRECTION")}${formatAttemptsLabel(
        getTotalAttemptsCount(module),
        translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.ATTEMPT.GENERIC")
      )}`,
      icon: "dst-icon-clock",
      variant: "warning",
      sortValue: _getModuleStatusSortValue("waiting_correction"),
    };
  }

  if (moduleHasExamWithStatus(module, EXAM_STATUSES.IN_PROGRESS)) {
    return {
      label: `${translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.IN_PROGRESS")}${formatAttemptsLabel(
        _getTotalFailedAttemptsCount(module),
        translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.ATTEMPT.FAILED")
      )}`,
      icon: "dst-icon-clock",
      variant: "primary",
      sortValue: _getModuleStatusSortValue("in_progress"),
    };
  }

  return {
    label: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.NO_ATTEMPT"),
    icon: "dst-icon-hourglass-01",
    variant: "neutral",
    sortValue: _getModuleStatusSortValue("no_attempt"),
  };
};

export const fetchProgramManagementSprints = (
  cohortId: string,
  language: string,
  setSprints: Function,
  setLoading: Function
) => {
  setLoading(true);

  ApiService.get(`cohorts/${cohortId}/program-management/overview`)
    .then((resp) => setSprints(resp.data.sprints))
    .catch((error) => {
      console.error("Error fetching program management sprints:", error);
      alert(translate(language, "ERROR.API_ERROR"));
    })
    .finally(() => setLoading(false));
};
